import { Box, Typography, Paper, Button, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Badge } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import { EventAvailable, Search } from '@mui/icons-material';

import { ColumnTitle, multiTextRender, Pagination, ScrollbarCustom, WrapTable } from '../../../../components';
import { useState } from 'react';
import { useGetList } from '../../../../services/charge-sessions/useChargeSession';
import { convertToDateTime } from '../../../../utils/datetime';
import { formatPrice } from '../../../../utils/format';
import { IUserDetail } from '../../../../types/User';
import { capitalize } from 'lodash';
import Copyable from '../../../../components/Copyable';

const StatusColor: Record<string, 'success' | 'warning' | 'error'> = {
    completed: 'success',
    pending: 'warning',
    failed: 'error',
    approved: 'success',
    rejected: 'error',
    captured: 'warning',
    released: 'success',
}

export const Reservation = ({ userDetail }: { userDetail: IUserDetail }) => {

    const [page, setPage] = useState(1);

    const { data, isFetching } = useGetList(page, {
        by_type: 'upcoming_and_historical_reservation',
        user_id: userDetail._id,
    });

    return (
        <Box sx={{ color: 'white', p: 4 }}>
            <Stack direction="row" alignItems="center" mb={4} spacing={2}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 32, mr: 1 }}>
                        <EventAvailable style={{ fill: '#0EAD00', width: 32, height: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>Reservation</Typography>
                        <Typography variant="body2" color="gray">Get a detailed breakdown of user reservations.</Typography>
                    </Box>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<Search />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        Search
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        Download CSV
                    </Button>
                    <IconButton sx={{ borderRadius: 3, color: 'white', border: '1px solid #444', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        <FilterListIcon />
                    </IconButton>
                </Stack>
            </Stack>
            <Paper elevation={0} sx={{ bgcolor: '#191919', borderRadius: 4, p: 4 }}>
                <Box mb={2}>
                    <WrapTable p={"5px 21px"} isLoading={isFetching} height="100%">
                        <TableContainer>
                            <ScrollbarCustom autoHide>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow>
                                            <ColumnTitle sx={{ minWidth: 50 }} align="center">SN</ColumnTitle>
                                            <ColumnTitle>Booking Id</ColumnTitle>
                                            <ColumnTitle>Charger Name</ColumnTitle>
                                            <ColumnTitle sx={{ minWidth: 160 }} >Created Date</ColumnTitle>
                                            <ColumnTitle sx={{ minWidth: 160 }} >Booking Date</ColumnTitle>
                                            <ColumnTitle>Grace Period</ColumnTitle>
                                            <ColumnTitle>Deposit</ColumnTitle>
                                            <ColumnTitle>Payment Method</ColumnTitle>
                                            <ColumnTitle align='center'> Payment Status</ColumnTitle>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data?.data?.map((item: any, index: number) => (
                                            <TableRow key={item._id}>
                                                <TableCell align="center">
                                                    {page * 10 - 10 + index + 1}
                                                </TableCell>
                                                <TableCell>
                                                    <Copyable text={item.ampeco_session_id || item._id} />
                                                </TableCell>
                                                <TableCell>{item.evse?.name || '-'}</TableCell>
                                                <TableCell>{convertToDateTime(item.createdAt)}</TableCell>
                                                <TableCell>{convertToDateTime(item?.reserve_start)}</TableCell>
                                                <TableCell>{item?.evse?.reservation_grace_period} mins</TableCell>
                                                <TableCell>
                                                    {multiTextRender(
                                                        item.Transactions?.map(
                                                            (val: any) => formatPrice(val.amount),
                                                        ),
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        capitalize(item.Transactions.filter((val: any) => Boolean(val.payment_type))[0]?.payment_type)
                                                    }
                                                </TableCell>
                                                <TableCell align='center'>

                                                    {
                                                        item.Transactions?.map((val: any, index: number) => (
                                                            <>
                                                                <Badge
                                                                    key={val._id}
                                                                    badgeContent={capitalize(val.status?.replace('_', ' '))}
                                                                    color={StatusColor[val.status?.toLowerCase()] || 'default'}
                                                                    sx={{
                                                                        mr: 1, mb: index !== item.Transactions.length - 1 ? 1 : 0, '& .MuiBadge-badge': {
                                                                            right: -15, top: 13, padding: '0 4px', minWidth: 'fit-content',

                                                                        }
                                                                    }}
                                                                />
                                                                {index < item.Transactions.length - 1 && <br />}
                                                            </>
                                                        ))
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ScrollbarCustom>
                        </TableContainer>
                    </WrapTable>
                    <Pagination
                        page={page}
                        setPage={setPage}
                        totalItems={data?.total || 0}
                        totalPage={Math.ceil((data?.total || 0) / 10)}
                        currentPage={page}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
